import {
  Box,
  Button,
  Checkbox,
  Divider,
  Flex,
  Icon,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spacer,
  Spinner,
  Stack,
  Text,
  TextProps,
  useDisclosure,
  UseDisclosureProps,
} from '@chakra-ui/react';
import { DataTableFilter, DataTableFilters } from '@pelicargo/datatable';
import { Show } from '@pelicargo/ui';
import {
  getPrettyAircraftType,
  getPrettyVariantType,
  modelUtils,
  prettyCurrency,
  prettyScheduleDateTime,
} from '@pelicargo/utils';
import { createColumnHelper } from '@tanstack/react-table';
import { addDays, differenceInDays, endOfDay, isAfter, isSameDay, startOfDay } from 'date-fns';
import { format } from 'date-fns-tz';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { HiChevronDown } from 'react-icons/hi';
import { HiBarsArrowDown, HiCheck } from 'react-icons/hi2';
import { useNavigate } from 'react-router-dom';

import { trpc, TrpcInputs, TrpcOutputs } from '../config/trpc';
import { useSelectedQuoteVariant } from '../hooks/useSelectedQuoteVariant';
import { API } from '../utils/apiTypes';
import { AlternativeFlightTableRow, mapAlternativeFlightItemsToTableRows } from '../utils/map-alternative-flight-items';
import { AirportStops } from './AirportStops';
import { QuoteScheduleDataTable } from './QuoteScheduleDataTable';
import { SimpleForm } from './SimpleForm';
import { TabButtons } from './TabButtons';

const ALL_SERVICE_TYPES = 'all-service-types';
const EMPTY_FILTERS: DataTableFilter[] = [];

const ScheduleColumnHeader = ({ label, ...textProps }: TextProps & { label: string }) => (
  <Text
    as="span"
    textTransform="uppercase"
    fontWeight="600"
    letterSpacing="0.06em"
    color="gray.700"
    bg="gray.100"
    {...textProps}
  >
    {label}
  </Text>
);
const ScheduleDateTimeCell = ({ date, timeZone }: { date: Date | string | null | undefined; timeZone?: string }) => {
  const parts = prettyScheduleDateTime(date, timeZone);
  if (!parts) return null;
  return (
    <Flex alignItems="center" gap="0.5em">
      <Text as="span" minW={24} textAlign="right">
        {parts.date}
      </Text>
      -
      <Text as="span" minW={12} textAlign="left">
        {parts.time}
      </Text>
    </Flex>
  );
};

const getAircraftTypeValue = (
  flightPath: AlternativeFlightTableRow['flight_path'] | undefined,
) =>
  (flightPath ?? [])
    .map((segment) => getPrettyAircraftType(segment.aircraft_type))
    .find(Boolean) ?? '';

const SERVICE_TYPE_TABS = [
  { label: 'All service types', value: ALL_SERVICE_TYPES },
  { label: 'General', value: 'General' },
  { label: 'Expedited', value: 'Expedited' },
] as const;

type AlternativeFlightSortMode = 'flight_path' | 'earliest' | 'cheapest';

const ALTERNATIVE_FLIGHT_SORT_LABEL: Record<AlternativeFlightSortMode, string> = {
  flight_path: 'Default',
  earliest: 'Earliest',
  cheapest: 'Cheapest',
};

type Props = UseDisclosureProps & {
  quote: TrpcOutputs['getQuote'] | API.QuoteWithTotalCost;
};

/** Router input is inferred as `void | object` for this procedure; exclude `void` for a mutable object type. */
type GetAlternativeFlightsInput = Exclude<TrpcInputs['getAlternativeFlights'], void>;
type GetAlternativeFlightsScheduleBoundsInput = Exclude<TrpcInputs['getAlternativeFlightsScheduleBounds'], void>;

type TableColumns = Partial<AlternativeFlightTableRow> & {
  aircraft?: undefined;
};

type FilterFormValues = {
  filters: DataTableFilter[];
  selectedServiceType: string;
  isNonstopOnly: boolean;
  isFreighterAndTrucksOnly: boolean;
  alternativeFlightSort: AlternativeFlightSortMode;
};

const DEFAULT_FILTER_FORM_VALUES: FilterFormValues = {
  filters: [],
  selectedServiceType: ALL_SERVICE_TYPES,
  isNonstopOnly: false,
  isFreighterAndTrucksOnly: false,
  alternativeFlightSort: 'flight_path',
};

export const QuoteScheduleModal = ({ quote, ...discloserProps }: Props) => {
  const { selectedQuoteVariantId, setSelectedQuoteVariantId } = useSelectedQuoteVariant();
  const originTimezone = useMemo(() => quote?.flight_path?.[0]?.origin_airport?.time_zone, [quote]);
  const destinationTimezone = useMemo(() => quote?.flight_path?.slice(-1)?.[0]?.destination_airport?.time_zone, [quote]);
  const { request_id: requestId, provider_id: providerId } = quote;
  const navigate = useNavigate();

  const dateSlidersSeededForBoundsKeyRef = useRef<string | null>(null);

  const methods = useForm<FilterFormValues>({
    defaultValues: DEFAULT_FILTER_FORM_VALUES,
  });
  const watchedFilters = useWatch({ control: methods.control, name: 'filters' });
  const filters = watchedFilters ?? EMPTY_FILTERS;
  const selectedServiceType = useWatch({ control: methods.control, name: 'selectedServiceType' }) ?? ALL_SERVICE_TYPES;
  const isNonstopOnly = useWatch({ control: methods.control, name: 'isNonstopOnly' }) ?? false;
  const isFreighterAndTrucksOnly =
    useWatch({ control: methods.control, name: 'isFreighterAndTrucksOnly' }) ?? false;
  const alternativeFlightSort =
    useWatch({ control: methods.control, name: 'alternativeFlightSort' }) ?? 'flight_path';

  const [showAllFlights, setShowAllFlights] = useState(false);
  const serviceTypeOptions = useMemo(() => SERVICE_TYPE_TABS, []);
  const scheduleDisclosure = useDisclosure(discloserProps);

  const alternativeFlightsScheduleBoundsInput = useMemo((): GetAlternativeFlightsScheduleBoundsInput => {
    const input: GetAlternativeFlightsScheduleBoundsInput = {
      request_id: requestId ?? 0,
      provider_id: providerId ?? '',
    };
    if (selectedServiceType !== ALL_SERVICE_TYPES) {
      input.quote_variant_type = selectedServiceType as GetAlternativeFlightsScheduleBoundsInput['quote_variant_type'];
    }
    return input;
  }, [requestId, providerId, selectedServiceType]);

  const alternativeFlightsInput = useMemo((): GetAlternativeFlightsInput => {
    const input: GetAlternativeFlightsInput = {
      request_id: requestId ?? 0,
      provider_id: providerId ?? '',
    };
    if (selectedServiceType !== ALL_SERVICE_TYPES) {
      input.quote_variant_type = selectedServiceType as GetAlternativeFlightsInput['quote_variant_type'];
    }
    if (!showAllFlights) {
      input.limit = 10;
      input.offset = 0;
    }

    const departsFilter = filters.find((filter) => filter.name === 'departs_at');
    const arrivesFilter = filters.find((filter) => filter.name === 'arrives_at');

    if (
      departsFilter &&
      Array.isArray(departsFilter.value) &&
      departsFilter.value.length === 2 &&
      departsFilter.value[0] &&
      departsFilter.value[1]
    ) {
      input.departs_at_gte = startOfDay(departsFilter.value[0] as Date);
      input.departs_at_lte = endOfDay(departsFilter.value[1] as Date);
    }

    if (
      arrivesFilter &&
      Array.isArray(arrivesFilter.value) &&
      arrivesFilter.value.length === 2 &&
      arrivesFilter.value[0] &&
      arrivesFilter.value[1]
    ) {
      input.arrives_at_gte = startOfDay(arrivesFilter.value[0] as Date);
      input.arrives_at_lte = endOfDay(arrivesFilter.value[1] as Date);
    }

    if (isNonstopOnly) {
      input.nonstop_only = true;
    }
    if (isFreighterAndTrucksOnly) {
      input.freighter_and_trucks_only = true;
    }

    input.sort = alternativeFlightSort;

    return input;
  }, [
    requestId,
    providerId,
    filters,
    selectedServiceType,
    showAllFlights,
    alternativeFlightSort,
    isNonstopOnly,
    isFreighterAndTrucksOnly,
  ]);

  const { data, isFetching, refetch: refetchAlternativeFlights } = trpc.getAlternativeFlights.useQuery(alternativeFlightsInput, {
    queryKey: ['getAlternativeFlights', alternativeFlightsInput],
    enabled: Boolean(requestId && providerId),
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  });

  const { data: scheduleBounds, refetch: refetchScheduleBounds } = trpc.getAlternativeFlightsScheduleBounds.useQuery(
    alternativeFlightsScheduleBoundsInput,
    {
      queryKey: ['getAlternativeFlightsScheduleBounds', alternativeFlightsScheduleBoundsInput],
      enabled: Boolean(requestId && providerId),
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    },
  );

  useEffect(() => {
    if (!scheduleDisclosure.isOpen) return;
    methods.setValue('selectedServiceType', ALL_SERVICE_TYPES);
    methods.setValue('isNonstopOnly', false);
    methods.setValue('isFreighterAndTrucksOnly', false);
    methods.setValue('alternativeFlightSort', 'flight_path');
    // methods.setValue('filters.0.value', [null, null]);
    // methods.setValue('filters.1.value', [null, null]);
    setShowAllFlights(false);
    refetchAlternativeFlights();
    refetchScheduleBounds();
  }, [methods, refetchAlternativeFlights, refetchScheduleBounds, scheduleDisclosure.isOpen]);


  const onRowClick = useCallback(
    (row: TableColumns) => {
      const doesVariantBelongToQuote = quote?.quote_variants?.some((variant) => variant.id === row.id);
      // In the awful world that we call "single page applications", navigate just changes page state, and the value persists.
      // Thus we must always set it
      setSelectedQuoteVariantId(row.id);

      // If the variant does not belong to the quote, navigate to the proper quote quote variant page
      if (!doesVariantBelongToQuote) {
        navigate(`/requests/${quote?.request_id}/quotes/${row.quote_id}/${row.id}`);
        scheduleDisclosure.onClose();
        return;
      }

      scheduleDisclosure.onClose();
    },
    [navigate, quote?.quote_variants, quote?.request_id, scheduleDisclosure, setSelectedQuoteVariantId],
  );

  const columnHelper = createColumnHelper<TableColumns>();
  const columns = useMemo(
    () => [
      columnHelper.accessor('flight_path', {
        header: () => <ScheduleColumnHeader label="Flight path" />,
        cell: (info) => {
          const isSelected = selectedQuoteVariantId === Number(info.row.original.id);

          return (
            <Flex alignItems="center">
              <Icon as={HiCheck} aria-hidden fontSize="xl" visibility={isSelected ? 'visible' : 'hidden'} width="14" />
              <AirportStops flightSegments={info.getValue()} w="full" minW="28" />
            </Flex>
          );
        },
      }),
      columnHelper.accessor('duration', {
        header: () => <ScheduleColumnHeader label="Duration" />,
        cell: (info) => {
          const row = info.row.original;
          if (!row.departs_at || !row.arrives_at) return '-';
          const trip = modelUtils.flightSegment.duration({
            departs_at: new Date(row.departs_at),
            arrives_at: new Date(row.arrives_at),
          });
          return trip?.prettyDuration ?? '-';
        },
      }),
      columnHelper.accessor('aircraft', {
        header: () => <ScheduleColumnHeader label="AC type" />,
        cell: (info) => (
          <Flex alignItems="center" justify="space-between">
            <Text>{getAircraftTypeValue(info.row.original.flight_path)}</Text>
            <Divider orientation="vertical" height={9} backgroundColor="border.default" />
          </Flex>
        )
      }),
      columnHelper.accessor('departs_at', {
        header: () => <ScheduleColumnHeader label="Departure" w="full" ml="4" />,
        cell: (info) => {
          return (
            <Flex gap="1">
              <ScheduleDateTimeCell date={info.getValue()} timeZone={originTimezone} />
            </Flex>
          );
        },
      }),
      columnHelper.accessor('arrives_at', {
        header: () => <ScheduleColumnHeader label="Arrival" w="full" ml="4" />,
        cell: (info) => {
          return (
            <Flex gap="1">
              <ScheduleDateTimeCell date={info.getValue()} timeZone={destinationTimezone} />
              <Divider orientation="vertical" height={9} backgroundColor="border.default" />
            </Flex>
          );
        },
      }),
      columnHelper.accessor('type', {
        header: () => <ScheduleColumnHeader label="Service" />,
        cell: (info) => {
          const data = info.getValue();
          const label = getPrettyVariantType(data);

          return (
            <Flex alignItems="center" gap="2">
              {label}
            </Flex>
          );
        },
      }),
      columnHelper.accessor('rate', {
        header: () => <ScheduleColumnHeader label="Rate" />,
        cell: (info) => {
          const rate = info.getValue();

          return (
            <Stack direction="row" alignItems="center" fontSize="inherit" lineHeight="inherit">
              <Text fontSize="inherit" lineHeight="inherit">
                {prettyCurrency(rate)}/kg
              </Text>
            </Stack>
          );
        },
      }),
    ],
    [columnHelper, selectedQuoteVariantId, originTimezone, destinationTimezone],
  );

  const boundsKey = `${requestId ?? ''}:${providerId ?? ''}:${selectedServiceType}`;

  useEffect(() => {
    if (!scheduleBounds) return;
    if (dateSlidersSeededForBoundsKeyRef.current === boundsKey) return;

    dateSlidersSeededForBoundsKeyRef.current = boundsKey;

    const now = new Date();
    const minDepart = scheduleBounds.min_departs_at ? new Date(scheduleBounds.min_departs_at) : now;
    const maxDepart = scheduleBounds.max_departs_at ? new Date(scheduleBounds.max_departs_at) : now;
    const minArrive = scheduleBounds.min_arrives_at ? new Date(scheduleBounds.min_arrives_at) : now;
    const maxArrive = scheduleBounds.max_arrives_at ? new Date(scheduleBounds.max_arrives_at) : now;
    const previousFilters = methods.getValues('filters');

    methods.setValue('filters', [
      {
        name: 'departs_at',
        label: 'Departs',
        value: [null, null],
        operator: 'gte',
        mode: 'insensitive',
        showOperator: false,
        inputType: 'date-range-slider',
        type: 'date-range-slider',
        minValue: minDepart,
        maxValue: maxDepart,
        toIndex: (value: unknown) => differenceInDays(value as Date, minDepart),
        fromIndex: (indexValue: number) => addDays(minDepart, indexValue),
        formatValue: (value: unknown) => format(value as Date, 'MMM d'),
        isEqual: (left: unknown, right: unknown) => isSameDay(left as Date, right as Date),
        anyLabel: 'ANY DATE',
        hideLabel: true,
      },
      {
        name: 'arrives_at',
        label: 'Arrives',
        value: [null, null],
        operator: 'gte',
        mode: 'insensitive',
        showOperator: false,
        inputType: 'date-range-slider',
        type: 'date-range-slider',
        minValue: minArrive,
        maxValue: maxArrive,
        toIndex: (value: unknown) => differenceInDays(value as Date, minArrive),
        fromIndex: (indexValue: number) => addDays(minArrive, indexValue),
        formatValue: (value: unknown) => format(value as Date, 'MMM d'),
        isEqual: (left: unknown, right: unknown) => isSameDay(left as Date, right as Date),
        anyLabel: 'ANY DATE',
        hideLabel: true,
      },
      ...previousFilters.filter((filter) => filter.name !== 'departs_at' && filter.name !== 'arrives_at'),
    ]);
  }, [boundsKey, methods, scheduleBounds]);

  const filteredData = useMemo(() => {
    const rows = mapAlternativeFlightItemsToTableRows(data?.items);
    if (!filters.length) return rows;

    return rows.filter((row) => {
      return filters.every((filter) => {
        const { name, operator, value } = filter;
        const rowValue = row?.[name];

        if (filter.name === 'departs_at' || filter.name === 'arrives_at') {
          return true;
        }
        if (operator === 'in') {
          return Array.isArray(value) && [...value].includes(rowValue);
        }
        if (operator === 'gte') {
          return isAfter(rowValue, startOfDay(new Date(value as Date | string)));
        }
        return true;
      });
    });
  }, [data?.items, filters]);

  const handleServiceTypeTabChange = useCallback((value: string) => {
    methods.setValue('selectedServiceType', value);
  }, [methods]);

  const handleAlternativeFlightSortChange = useCallback((mode: AlternativeFlightSortMode) => {
    methods.setValue('alternativeFlightSort', mode);
  }, [methods]);

  const handleFiltersChange = useCallback((nextFilters: DataTableFilter[] | ((prevFilters: DataTableFilter[]) => DataTableFilter[])) => {
    const previousFilters = methods.getValues('filters');
    const resolvedFilters = typeof nextFilters === 'function' ? nextFilters(previousFilters) : nextFilters;
    methods.setValue('filters', resolvedFilters);
  }, [methods]);

  useEffect(() => {
    setShowAllFlights(false);
  }, [selectedServiceType, filters, isNonstopOnly, isFreighterAndTrucksOnly]);

  return (
    <Modal {...scheduleDisclosure} size="7xl">
      <ModalOverlay />
      <ModalContent borderRadius="lg" overflow={'hidden'}>
        <ModalHeader pb="0">
          <Flex justifyContent="space-between" pr={16}>
            <Flex>Alternative Flights</Flex>
          </Flex>
        </ModalHeader>
        <ModalCloseButton />
        <SimpleForm<FilterFormValues> submitOnBlur={false} {...methods}>
          <ModalBody h="full" overflow="hidden" p="0">
            <Stack spacing="4" h="full" minH="0">
              <Box px="4">
                <Flex justifyContent="center" pb={8}>
                  <TabButtons
                    options={serviceTypeOptions}
                    value={selectedServiceType}
                    onChange={handleServiceTypeTabChange}
                    maxW="4xl"
                  />
                </Flex>
                <Flex alignItems="flex-end" gap={4} w="full" flexWrap="wrap" >
                  <DataTableFilters filters={filters} setFilters={handleFiltersChange} isClearable={false} />
                  <Flex direction="row" gap={4}>
                    <Checkbox
                      isChecked={isNonstopOnly}
                      onChange={(event) => methods.setValue('isNonstopOnly', event.target.checked)}
                    >
                      <Text >Nonstop</Text>
                    </Checkbox>
                    <Checkbox
                      size="md"
                      isChecked={isFreighterAndTrucksOnly}
                      onChange={(event) => methods.setValue('isFreighterAndTrucksOnly', event.target.checked)}
                    >
                      <Text >Freighter & Trucks</Text>
                    </Checkbox>
                  </Flex>
                  <Spacer display={{ base: 'none', md: 'block' }} />
                  <Menu placement="bottom-end" gutter={4}>
                    <MenuButton
                      as={Button}
                      bgColor="bg.surface"
                      borderColor="border.default"
                      borderWidth={1}
                      borderRadius="md"
                      fontWeight={400}
                      leftIcon={<Icon as={HiBarsArrowDown} aria-hidden />}
                      rightIcon={<HiChevronDown aria-hidden />}
                      aria-label={`Sort alternative flights. Current sort: ${ALTERNATIVE_FLIGHT_SORT_LABEL[alternativeFlightSort]}`}
                      _hover={{ borderColor: 'border.hover', bgColor: 'bg.surface' }}
                      _active={{ borderColor: 'border.focused', bgColor: 'bg.surface' }}
                    >
                      {`Sort: ${ALTERNATIVE_FLIGHT_SORT_LABEL[alternativeFlightSort]}`}
                    </MenuButton>
                    <MenuList zIndex={2} minW="40">
                      <MenuItem
                        onClick={() => handleAlternativeFlightSortChange('flight_path')}
                        aria-current={alternativeFlightSort === 'flight_path' ? 'true' : undefined}
                      >
                        Default
                      </MenuItem>
                      <MenuItem
                        onClick={() => handleAlternativeFlightSortChange('earliest')}
                        aria-current={alternativeFlightSort === 'earliest' ? 'true' : undefined}
                      >
                        Earliest
                      </MenuItem>
                      <MenuItem
                        onClick={() => handleAlternativeFlightSortChange('cheapest')}
                        aria-current={alternativeFlightSort === 'cheapest' ? 'true' : undefined}
                      >
                        Cheapest
                      </MenuItem>
                    </MenuList>
                  </Menu>
                </Flex>
              </Box>
              <Flex flex="1" minH="0">
                <Box position="relative" w="full" bg="bg.default">
                  <QuoteScheduleDataTable
                    columns={columns}
                    maxH={730}
                    data={filteredData}
                    dataCount={data?.total_count}
                    columnVisibility={{ id: false }}
                    onRowClick={onRowClick}
                    hidePagination
                  />
                  <Show if={filteredData.length < data?.total_count && data?.total_count > 10}>
                    <Flex justifyContent="center" pb={4} height={16}>
                      <Button size="md" variant="link" color="primary.500" onClick={() => setShowAllFlights(true)}>
                        {`View all flights (${data?.total_count})`}
                      </Button>
                    </Flex>
                  </Show>
                  <Show if={isFetching}>
                    <Flex
                      position="absolute"
                      inset="0"
                      alignItems="center"
                      justifyContent="center"
                      bg="blackAlpha.100"
                      borderRadius="md"
                      zIndex={2}
                    >
                      <Spinner size="lg" color="orange.400" />
                    </Flex>
                  </Show>
                </Box>
              </Flex>

            </Stack>
          </ModalBody>
        </SimpleForm>
      </ModalContent>
    </Modal>
  );
};
